import api from './api';
import axios from 'axios'; // Import axios for comparison
import { WorkingState, DiagnosticProbe, ProbeRunRecord } from '../types/persona_engine';

// Add debugging to compare api vs axios
console.error("personaEngineeringService.ts: api.defaults.baseURL =", api.defaults.baseURL);
console.error("personaEngineeringService.ts: axios.defaults.baseURL =", axios.defaults.baseURL);

const API_BASE = '/persona-engine/engineering';

/**
 * Generate a new persona from an intent.
 * 
 * @param intent User's description of the desired persona
 * @returns WorkingState with identity core populated
 */
export async function genesis(intent: string): Promise<WorkingState> {
  const response = await api.post(`${API_BASE}/genesis`, { intent });
  return response.data;
}

/**
 * Modify the persona's identity core based on a user instruction.
 * 
 * @param state Current working state
 * @param instruction User's instruction to modify the persona
 * @returns Updated WorkingState with modified identity core
 */
export async function mutateSource(state: WorkingState, instruction: string): Promise<WorkingState> {
  const response = await api.post(`${API_BASE}/source/mutate`, { 
    state, 
    instruction 
  });
  return response.data;
}

/**
 * Generate UI controls based on the controller prompt.
 * 
 * @param state Current working state
 * @param instruction Optional instruction for control generation
 * @returns Updated WorkingState with UI schema populated
 */
export async function generateControls(
  state: WorkingState, 
  instruction?: string
): Promise<WorkingState> {
  const response = await api.post(`${API_BASE}/controls/generate`, { 
    state, 
    instruction 
  });
  return response.data;
}

/**
 * Compile the controller prompt and UI values into a system prompt.
 * 
 * @param state Current working state
 * @returns Updated WorkingState with system_prompt populated
 */
export async function compile(state: WorkingState): Promise<WorkingState> {
  const response = await api.post(`${API_BASE}/compile`, { state });
  return response.data;
}

/**
 * Test the persona with a chat message.
 * 
 * @param state Current working state
 * @param message User's message to the persona
 * @returns Assistant's reply
 */
export async function testdriveChat(state: WorkingState, message: string): Promise<string> {
  const response = await api.post(`${API_BASE}/testdrive/chat`, { 
    state, 
    message 
  });
  // Return just the reply string, not the full response object
  return response.data.reply;
}

/**
 * Bake the current parameter values into a new baseline.
 * 
 * @param state Current working state
 * @returns New WorkingState with parameters baked into identity
 */
export async function bake(state: WorkingState): Promise<WorkingState> {
  const response = await api.post(`${API_BASE}/bake`, { state });
  return response.data;
}

/**
 * JIT probe generation — reads latest saved state from DB.
 * Returns only the new probes; frontend merges into local state.
 */
export async function generateProbes(
  personaId: string,
  count: number = 5,
  focusAreas?: string[],
  existingProbeIds?: string[],
): Promise<DiagnosticProbe[]> {
  const response = await api.post(`${API_BASE}/probes/generate/${personaId}`, {
    count,
    focus_areas: focusAreas,
    existing_probe_ids: existingProbeIds,
  });
  return response.data;
}

/**
 * Auto-persist a probe run result immediately after user judgment.
 * Non-fatal if it fails — state is updated locally and will persist on next save.
 */
export async function recordProbeRun(
  personaId: string,
  probeId: string,
  runRecord: ProbeRunRecord,
): Promise<void> {
  await api.post(`${API_BASE}/probes/record-run`, {
    persona_id: personaId,
    probe_id: probeId,
    run_record: runRecord,
  });
}

/**
 * Extract a rich capability report card from a compiled persona system prompt.
 *
 * @param state Current working state (must have system_prompt populated)
 * @param count Number of capabilities to extract (default 12)
 * @param depth Report depth: 'brief' | 'standard' | 'deep'
 * @returns Array of CapabilityEntry objects
 */
export async function extractCapabilities(
  state: WorkingState,
  count: number = 12,
  depth: 'brief' | 'standard' | 'deep' = 'standard'
): Promise<import('../types/persona_engine').CapabilityEntry[]> {
  const response = await api.post(`${API_BASE}/capabilities/extract`, {
    state,
    count,
    depth,
  }, { timeout: 180_000 }); // 3 min — LLM call with 12 caps + reports is slow
  return response.data;
}

/**
 * Publish the persona to the database.
 *
 * @param state Current working state
 * @param personaId Optional ID of an existing persona to update
 * @returns ID of the published persona
 */
export async function publish(state: WorkingState, personaId?: string): Promise<string> {
  // Add detailed logging
  console.error("PUBLISH DEBUG:");
  console.error(`API_BASE: ${API_BASE}`);
  console.error(`Full URL being constructed: ${API_BASE}/publish`);
  console.error(`api.defaults.baseURL: ${api.defaults.baseURL}`);
  console.error(`axios.defaults.baseURL: ${axios.defaults.baseURL}`);
  
  try {
    // Ensure the API_BASE has a leading slash
    const endpoint = API_BASE.startsWith('/') 
      ? `${API_BASE}/publish` 
      : `/${API_BASE}/publish`;
      
    console.error(`Modified endpoint: ${endpoint}`);
    
    const response = await api.post(endpoint, { 
      state, 
      persona_id: personaId 
    });
    console.error("Publish response:", response);
    return response.data.persona_id;
  } catch (error) {
    console.error("Publish error:", error);
    console.error("Error request config:", error.config);
    console.error("Full URL that failed:", error.config?.url);
    throw error;
  }
}
